// CountSolutionsForAbsoluteTask.js
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";

// === OPCJE ===
const options = [
  { value: "0", label: "brak rozwiązań" },
  { value: "1", label: "jedno rozwiązanie" },
  { value: "2", label: "dwa rozwiązania" }
];

// === GENERUJ ===
export function generateTask() {
  let a, b, d, e, c;
  const allowedD = [2, 3, 4, 5];

  // Losuj a ≠ 0
  do {
    a = Math.floor(Math.random() * 4 + 1) * (Math.random() < 0.5 ? -1 : 1);
  } while (a === 0);

  // Losuj pozostałe składniki
  b = Math.floor(Math.random() * 6 + 1) * (Math.random() < 0.5 ? -1 : 1);
  d = allowedD[Math.floor(Math.random() * allowedD.length)];
  e = Math.floor(Math.random() * 9 + 1) * (Math.random() < 0.5 ? -1 : 1);

  // Losuj typ rozwiązania z równym prawdopodobieństwem
  const types = ["negative", "zero", "positive"];
  const type = types[Math.floor(Math.random() * types.length)];

  let val;
  if (type === "negative") {
    // wartość bezwzględna = liczba ujemna → brak rozwiązań
    val = -Math.floor(Math.random() * 5 + 1);
  } else if (type === "zero") {
    // wartość bezwzględna = 0 → jedno rozwiązanie
    val = 0;
  } else {
    // wartość bezwzględna = liczba dodatnia → dwa rozwiązania
    val = Math.floor(Math.random() * 5 + 1);
  }

  c = d * val + e;
  const numSolutions = val < 0 ? "0" : val === 0 ? "1" : "2";

  // Przygotuj wyrażenie LaTeX
  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const bPart = b > 0 ? `+ ${b}` : b < 0 ? `- ${Math.abs(b)}` : "";
  const absExpr = `\\left|${ax} ${bPart}\\right|`;
  const fullExpr = `${d}${absExpr} ${e >= 0 ? "+ " + e : "- " + Math.abs(e)} = ${c}`;
  const latex = fullExpr.replace(/\\/g, "\\\\");

  return {
    type: "CountSolutionsForAbsoluteTask",
    data: {
      a, b, d, e, c, numSolutions, latex
    }
  };
}




export function renderTask({ data }) {
  const { a, b, d, e, c } = data;

  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const bPart = b > 0 ? `+ ${b}` : b < 0 ? `- ${Math.abs(b)}` : "";
  const latex = `${d}\\left|${ax} ${bPart}\\right| ${e >= 0 ? "+ " + e : "- " + Math.abs(e)} = ${c}`;

  return (
    <p>
      Określ liczbę rozwiązań równania: <InlineMath math={latex} />
    </p>
  );
}


// === INPUT ===
export function renderInput(_, userInput, setUserInput, disabled) {
  return (
    <SelectInput
      options={options}
      value={userInput}
      onChange={e => setUserInput(e?.target?.value ?? e)}
      placeholder="Wybierz liczbę rozwiązań"
      disabled={disabled}
    />
  );
}

// === OCENA ===
export function checkAnswer({ data }, userInput) {
  const correct = userInput === data.numSolutions;
  return {
    correct,
    expected: [data.numSolutions]
  };
}

// === TWOJA ODPOWIEDŹ ===
export function renderUserAnswer(userInput) {
  const label = options.find(o => o.value === userInput)?.label;
  return label ? <span><strong>{label}</strong></span> : <i>(brak odpowiedzi)</i>;
}

// === POPRAWNA ODPOWIEDŹ ===
export function renderAnswer({ data }) {
  const label = options.find(o => o.value === data.numSolutions)?.label;
  return label ? <span><strong>{label}</strong></span> : null;
}

// === WYJAŚNIENIE ===
export function renderExplanation({ data }) {
  const { a, b, d, e, c, numSolutions } = data;

  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const bPart = b > 0 ? `+ ${b}` : b < 0 ? `- ${Math.abs(b)}` : "";
  const absExpr = `\\left|${ax} ${bPart}\\right|`;
  const fullExpr = `${d}${absExpr} ${e >= 0 ? "+ " + e : "- " + Math.abs(e)} = ${c}`;

  const rightSide = c - e;
  const simplified = `\\dfrac{${rightSide}}{${d}}`;
  const simplifiedValRaw = rightSide / d;
  const simplifiedVal = Number.isInteger(simplifiedValRaw)
    ? simplifiedValRaw
    : simplifiedValRaw.toFixed(2);

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Zaczynamy od przepisania równania:</p>
      <BlockMath math={fullExpr} />
      <BlockMath math={`${d}${absExpr} = ${rightSide}`} />

      <p><strong>Krok 2:</strong> Dzielimy przez <InlineMath math={String(d)} />:</p>
      <BlockMath math={`${absExpr} = ${simplified}`} />
      <BlockMath math={`${absExpr} = ${simplifiedVal}`} />

      <p><strong>Krok 3:</strong> Wnioskujemy:</p>
      {numSolutions === "2" && (
        <BlockMath math={`\\text{Ponieważ prawa strona } > 0, \\text{ są dwa rozwiązania.}`} />
      )}
      {numSolutions === "1" && (
        <BlockMath math={`\\text{Ponieważ prawa strona } = 0, \\text{ jest jedno rozwiązanie.}`} />
      )}
      {numSolutions === "0" && (
        <BlockMath math={`\\text{Ponieważ prawa strona } < 0, \\text{ brak rozwiązań.}`} />
      )}
    </div>
  );
}

